import { useEffect, useState } from 'react';

/**
 * Overlaps smaller than this are a collapsing browser toolbar, not a keyboard.
 * iOS Safari's URL bar is ~50–90px; the smallest on-screen keyboard is ~250px.
 */
const KEYBOARD_MIN_INSET = 120;

/**
 * Height, in px, that the virtual keyboard hides at the bottom of the layout
 * viewport — 0 when no keyboard is open (or `visualViewport` is unsupported).
 *
 * Needed because `dvh` deliberately excludes the on-screen keyboard: it tracks
 * only the UA's own chrome, so a `100dvh` scene stays full height while the
 * keyboard covers its lower half. `interactive-widget=resizes-content` fixes
 * this at the viewport level where supported (Chrome/Android); this hook is the
 * fallback for engines that still only resize the VISUAL viewport (iOS Safari,
 * including standalone PWAs).
 *
 * When `interactive-widget=resizes-content` IS honored, the layout viewport
 * shrinks with the keyboard, so `innerHeight` and `visualViewport.height` fall
 * together and this returns 0 — no double compensation.
 *
 * `offsetTop` is subtracted because iOS scrolls the visual viewport within the
 * layout viewport; what it scrolls past is no longer hidden below the fold.
 */
export function useVirtualKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const recompute = () => {
      const hidden = window.innerHeight - viewport.height - viewport.offsetTop;
      setInset(hidden > KEYBOARD_MIN_INSET ? Math.round(hidden) : 0);
    };

    recompute();
    viewport.addEventListener('resize', recompute);
    viewport.addEventListener('scroll', recompute);

    return () => {
      viewport.removeEventListener('resize', recompute);
      viewport.removeEventListener('scroll', recompute);
    };
  }, []);

  return inset;
}
